/**
 * 商户信息（merchantId / merchantType / 商户组）统一存储层。
 *
 * 背景：merchantId 之前散落在各视图里直接读写 localStorage，并且在切换商户、
 * 拉取商户模式这类异步操作之前会先 removeItem('merchantId')，导致
 * 1) 请求返回前其它组件读到的 merchantId 是 null；
 * 2) 请求失败时 merchantId 被永久清掉，首页 /api/client/appIndexV2 拿不到商户。
 *
 * 约定：
 * - 读取统一走 getMerchantId()，内部带兜底，merchantId 被清掉时也不会立刻变成 null；
 * - 写入统一走 setMerchantInfo() / saveCustomerMerchant()，一次性落库；
 * - 清理统一走 clearMerchantInfo()。
 *
 * 本文件保持零依赖（不 import 任何 api / store），store 里也可以安全引用。
 */

export const MERCHANT_KEYS = {
  merchantId: 'merchantId',
  oldMerchantId: 'oldMerchantId',
  merchantType: 'merchantType',
  merchantTypeOld: 'merchantTypeOld',
  merchantGroupName: 'merchantGroupName',
  merchantListVos: 'merchantListVos',
  isAgg: 'isAgg',
  isCashier: 'isCashier',
  pageVisibleType: 'pageVisibleType'
}

/**
 * 读取当前商户 ID。
 * merchantId 缺失时兜底到 oldMerchantId（客户所属商户组的默认商户），
 * 保证不会因为 merchantId 被清掉就出现"商户丢失"。
 * 两者都没有时返回 null，交给请求层决定是否带参（避免发出 merchantId= 这种空参）。
 */
export function getMerchantId() {
  return localStorage.getItem(MERCHANT_KEYS.merchantId) ||
    localStorage.getItem(MERCHANT_KEYS.oldMerchantId) ||
    null
}

/** 读取当前模式：'agg' 聚合模式，其余为单店模式 */
export function getMerchantType() {
  return localStorage.getItem(MERCHANT_KEYS.merchantType) || ''
}

export function getMerchantInfo() {
  return {
    merchantId: getMerchantId(),
    merchantType: getMerchantType(),
    merchantGroupName: localStorage.getItem(MERCHANT_KEYS.merchantGroupName) || '',
    oldMerchantId: localStorage.getItem(MERCHANT_KEYS.oldMerchantId) || ''
  }
}

/**
 * 批量写入商户信息，避免逐个 setItem 时出现"改了一半"的中间态。
 * undefined 表示不改动该 key，null 表示删除该 key，其余按字符串写入。
 */
export function setMerchantInfo(info = {}) {
  Object.keys(info).forEach(key => {
    const value = info[key]
    if (value === undefined) return
    if (value === null) {
      localStorage.removeItem(key)
      return
    }
    localStorage.setItem(key, String(value))
  })
  return getMerchantInfo()
}

/**
 * 从 getCustomerMerchantType 的返回里取出商户 id。
 * 不同模式下服务端字段名不一致：聚合模式是 defMerId，单店模式可能是 merchantId / defMerchantId，
 * 这里按优先级兜底，避免"接口返回了商户但没落库"导致 merchantId 为空。
 */
function pickMerchantIdFromData(data) {
  const candidates = [data.defMerId, data.merchantId, data.defMerchantId]
  for (let i = 0; i < candidates.length; i++) {
    const v = candidates[i]
    if (v !== undefined && v !== null && v !== '') return String(v)
  }
  return ''
}

/**
 * 落库 /api/client/getCustomerMerchantType 的返回（单店 / 聚合模式 + 当前默认商户）。
 * defMerId 缺失时保留原有 merchantId，不要覆盖成空值。
 */
export function saveCustomerMerchant(data) {
  if (!data) return getMerchantInfo()
  const merchantType = data.type || getMerchantType()
  const picked = pickMerchantIdFromData(data)
  const merchantId = picked || getMerchantId()
  const merchantGroupName = merchantType === 'agg'
    ? (data.groupName || '')
    : (data.merchantName || '')
  const info = {
    merchantType: merchantType,
    merchantTypeOld: data.type || merchantType,
    merchantGroupName: merchantGroupName
  }
  // merchantId 为空时不要写入，避免把已有的商户清掉
  if (merchantId) {
    info.merchantId = merchantId
  }
  return setMerchantInfo(info)
}

/**
 * 退出登录 / 401 失效时清理全部商户相关缓存。
 */
export function clearMerchantInfo() {
  Object.keys(MERCHANT_KEYS).forEach(key => {
    localStorage.removeItem(MERCHANT_KEYS[key])
  })
}



// WEBPACK FOOTER //
// ./src/utils/merchant.js